<template>
  <section class="specialty-selector py-5 bg-light-gray">
    <div class="container text-center">
      <div class="row justify-content-center mb-4">
        <div class="col-lg-8">
          <span
            class="badge badge-primary-base px-3 py-2 text-uppercase
              font-weight-bold mb-2"
          >
            Hecho a la medida de tu consulta
          </span>
          <h2 class="h1 font-weight-bold text-dark-blue mb-3">
            Ajustado exactamente a tu especialidad médica
          </h2>
          <p class="lead text-muted">
            Selecciona tu área para ver cómo AMII se adapta a tus
            necesidades clínicas cotidianas.
          </p>
        </div>
      </div>

      <!-- Specialty Tabs -->
      <div class="d-flex flex-wrap justify-content-center mb-4 gap-2">
        <button
          v-for="spec in specialties"
          :key="spec.id"
          class="btn btn-sm rounded-pill font-weight-bold px-3 py-2
            transition-all m-1"
          :class="activeSpec === spec.id
            ? 'btn-primary-base shadow-sm'
            : 'btn-outline-secondary bg-white'"
          @click="activeSpec = spec.id"
        >
          <span class="mr-1">{{ spec.icon }}</span>
          {{ spec.name }}
        </button>
      </div>

      <!-- Active Specialty Detail Card -->
      <div class="row justify-content-center">
        <div class="col-lg-10">
          <div
            class="card bg-white p-4 p-md-5 rounded-xl shadow-sm
              text-left border-primary-light"
          >
            <div class="row align-items-center">
              <div class="col-md-7 mb-4 mb-md-0">
                <div class="d-flex align-items-center mb-3">
                  <span class="display-4 mr-3">
                    {{ currentSpecialty.icon }}
                  </span>
                  <div>
                    <h3 class="h3 font-weight-bold text-dark-blue mb-0">
                      {{ currentSpecialty.title }}
                    </h3>
                    <span class="text-success-base font-weight-bold small">
                      Optimizado para tu flujo de trabajo
                    </span>
                  </div>
                </div>

                <ul class="list-unstyled mb-0">
                  <li
                    v-for="(feature, idx) in currentSpecialty.features"
                    :key="idx"
                    class="d-flex align-items-start mb-3"
                  >
                    <font-awesome-icon
                      icon="check-circle"
                      class="text-success-base mt-1 mr-2"
                    />
                    <span class="text-dark">{{ feature }}</span>
                  </li>
                </ul>
              </div>

              <!-- Specialty Visual Preview Graphic -->
              <div class="col-md-5">
                <div class="p-4 bg-light-blue rounded-xl text-center border">
                  <img
                    :src="currentSpecialty.image"
                    :alt="currentSpecialty.title"
                    class="img-fluid rounded-lg mb-3 shadow-xs"
                    style="max-height: 180px; object-fit: cover; width: 100%;"
                  >
                  <div class="h5 font-weight-bold text-primary-dark mb-2">
                    Herramienta Clave
                  </div>
                  <p class="small text-muted mb-3">
                    {{ currentSpecialty.highlight }}
                  </p>
                  <div
                    class="badge badge-success-base p-2 font-weight-bold
                      d-block text-truncate"
                  >
                    ✨ Generación en 1 Clic
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'SpecialtySelector',
  data() {
    return {
      activeSpec: 'gineco',
      specialties: [
        {
          id: 'gineco',
          name: 'Ginecología y Obstetricia',
          icon: '👶',
          title: 'Ginecología & Obstetricia',
          features: [
            'Proyecciones hiperrealistas del rostro del bebé con IA.',
            'Informes ecográficos con imágenes incorporadas directamente.',
            'Colpogramas con cuadrantes para señalar lesiones en el reporte.',
            'Cálculo automático de percentiles de peso fetal.',
          ],
          highlight: 'Proyecciones hiperrealistas e informes ecográficos.',
          image: '/img/hiperrealismo.png',
        },
        {
          id: 'pediatria',
          name: 'Pediatría',
          icon: '🧸',
          title: 'Pediatría',
          features: [
            'Tablas de crecimiento talla/edad con percentiles automáticos.',
            'Muestra exacta de edad en años, meses y días.',
            'Carnet y registro completo de vacunación.',
            'Fórmulas configurables para dosis pediátricas e IMC.',
          ],
          highlight: 'Carnet de vacunas, edad exacta y dosificación.',
          image: '/img/soy-medico/screenshots/llenado-recipe.png',
        },
        {
          id: 'cirugia',
          name: 'Cirugía y Estética',
          icon: '🩺',
          title: 'Cirugía General, Plástica & Bariátrica',
          features: [
            'Plantillas de presupuestos quirúrgicos completables.',
            'Cálculo de IMC y peso ideal en segundos.',
            'Registro visual de fotos del Antes y Después.',
            'Informes para seguros médicos y constancias de reposo.',
          ],
          highlight: 'Presupuestos quirúrgicos autocalculados y fotos.',
          image: '/img/soy-medico/screenshots/perfil-dr-gallo.png',
        },
        {
          id: 'trauma',
          name: 'Traumatología & Oftalmología',
          icon: '🦴',
          title: 'Traumatología, Radiología & Oftalmología',
          features: [
            'Visor DICOM web integrado para leer Rx sin programas extra.',
            'Herramientas para medir longitudes, ángulos y áreas.',
            'Marcado libre sobre placas e imágenes radiológicas.',
            'Control y medición de glaucoma en Oftalmología.',
          ],
          highlight: 'Visor DICOM en la nube para medir y señalar Rx.',
          image: '/img/soy-medico/screenshots/galeria-pruebas-clinicas.png',
        },
        {
          id: 'interna',
          name: 'Medicina Interna & Neumonología',
          icon: '🫀',
          title: 'Medicina Interna & Neumonología',
          features: [
            'Cálculo automático de factores e índices (IPA, IMC, etc.).',
            'Valoraciones preoperatorias aceleradas.',
            'Obtención inmediata del riesgo de Lee, score Ariscat y más.',
            'Cálculo del resultado de fórmulas complejas como DAS28.',
          ],
          highlight: 'Escalas de riesgo preoperatorio e índices clínicos.',
          image: '/img/soy-medico/screenshots/toma-de-diagnostico.png',
        },
        {
          id: 'odontologia',
          name: 'Odontología',
          icon: '🦷',
          title: 'Odontología',
          features: [
            'Odontograma interactivo sobre imagen personalizada.',
            'Trazado libre de tratamientos y estado de cada pieza dental.',
            'Anotaciones visuales para explicar procedimientos al paciente.',
            'Presupuestos por tratamiento detallados.',
          ],
          highlight: 'Odontograma digital e historias clínicas visuales.',
          image: '/img/soy-medico/screenshots/citas-pendientes.png',
        },
        {
          id: 'otros',
          name: 'Otros...',
          icon: '🤝',
          title: 'Adaptable a cualquier Especialidad Médica',
          features: [
            'Cualquier especialidad puede adaptar sus parámetros en AMII.',
            'Campos clínicos, fichas y fórmulas 100% personalizables.',
            'Atención al cliente dedicada para acompañarte en el proceso.',
            'Plantillas a medida para tus récipes, informes y certificaciones.',
          ],
          highlight: 'Soporte y atención al cliente para adaptar AMII.',
          image: '/img/support.png',
        },
      ],
    };
  },
  computed: {
    currentSpecialty() {
      return this.specialties.find((s) => s.id === this.activeSpec)
        || this.specialties[0];
    },
  },
};
</script>

<style scoped>
.text-dark-blue {
  color: #002196;
}
.bg-light-blue {
  background-color: #f0f6ff;
}
.border-primary-light {
  border: 1px solid #d0e2ff;
}
</style>
